// Runtime environment injected by layout.tsx <script> tag.
// Allows same Docker image to work across environments (E2E, staging, production).
declare global {
  interface Window {
    __ENV?: {
      NEXT_PUBLIC_API_URL?: string;
      // Git release tag, injected at request time by the inline <script> in
      // layout.tsx. Used by grafana-observability.tsx to stamp Faro events
      // with app.version. Server-side process.env.RELEASE_TAG.
      RELEASE_TAG?: string;
    };
  }
}

/** Read a runtime env var: client reads window.__ENV, server reads process.env */
function getRuntimeEnv(key: string): string | undefined {
  // Client: read from window.__ENV (injected by layout.tsx)
  if (typeof window !== 'undefined' && window.__ENV) {
    return (
      (window.__ENV as Record<string, string | undefined>)[key] || undefined
    );
  }
  // Server: read from process.env
  return process.env[key] || undefined;
}

export const isDevelopment = process.env.NODE_ENV === 'development';
// export const isDevelopment = false;

// Launch promo (issue #4744): backend is the single source of truth.
// /internal/pc returns the active/plans/discount-pct payload — frontend
// reads it via Redux (selectLaunchPromo). The legacy
// NEXT_PUBLIC_LAUNCH_PROMO_ACTIVE / _PLANS env vars were removed; backend
// settings.LAUNCH_PROMO_ACTIVE + LAUNCH_PROMO_PLANS control both the
// strikethrough UI and the checkout coupon now.

// Server-safe site URL for metadata and OG images (no window access).
// Canonical host is the apex `lexomat.sk` — www redirects to apex (see next.config.mjs).
export const SITE_URL =
  process.env['NEXT_PUBLIC_SITE_URL'] || 'https://lexomat.sk';

// Brand Twitter/X handle — reused across root layout metadata, per-page
// Twitter Card metadata (see `buildTwitterMetadata` in seo-utils.ts), and
// structured-data sameAs URLs. Single source of truth prevents drift.
export const LEXOMAT_TWITTER_HANDLE = '@lexomat_sk';

function getRuntimeOrigin(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const origin = window.location?.origin;
  if (!origin || origin === 'null') {
    return null;
  }

  return origin;
}

export const DOMAIN = isDevelopment
  ? 'http://localhost:3000'
  : getRuntimeOrigin() || SITE_URL;

export const HOST =
  getRuntimeEnv('NEXT_PUBLIC_API_URL') ||
  (isDevelopment ? 'http://localhost:8080' : 'https://api.lexomat.sk');

// Public URL of the Lexomat Data Processing Agreement template. Org owners
// download it, sign it, and upload the signed copy via the OrgDpaUploadCard
// (used on /organization/legal and in org-onboarding step 3). Hosted in the
// public `lexomat-dpa` Supabase storage bucket so it's CDN-served and
// downloadable without auth. The default is the current v1.0 URL; bump
// NEXT_PUBLIC_DPA_TEMPLATE_URL when you publish a new template version.
export const DPA_TEMPLATE_URL =
  process.env['NEXT_PUBLIC_DPA_TEMPLATE_URL'] ||
  'https://baxhrzbuibtghirijdsc.supabase.co/storage/v1/object/public/lexomat-dpa/dpa.pdf';

// Private Supabase storage bucket where signed org DPAs land (browser
// uploads directly via supabase-js, gated by storage.objects RLS — see
// migration 20260507133001_add_dpa_storage_rls.sql). Must match the
// backend Settings.ORG_DPA_BUCKET_NAME default of "dpa-documents".
export const ORG_DPA_BUCKET = 'dpa-documents';

export const SlovLexUrl = 'https://www.slov-lex.sk/ezbierky/pravne-predpisy';

export const BACKEND_URL = {
  EMBEDDING: '/embedding',
  GET_RESPONSE: '/get-response',
  RESEARCH: '/conversation',
  SEARCH: '/search',
  PROFESSIONALS: '/professionals',
  LAW_FIRMS: '/law-firms',
  GEOCODING: '/geocoding',
  LEGISLATION: {
    BASE: '/legislation',
    HISTORY: '/history',
    REFERENCED_DECISIONS: '/referenced-decisions',
    REFERENCED_DECISIONS_COUNT: '/referenced-decisions/count',
    REDIRECT_LEGISLATION_IRI: '/redirect-legislation-iri',
    GET_LEGISLATION_INFO: '/get-legislation-info',
  },
};

export const DEFAULT_FILTERS_PAGE_SIZE = 20;

export const HIGHLIGHT_FILTER_COLOR = '#FFEB3B'; // Yellow color for highlighting search matches in filters

export const ReSEARCH_DATA_SOURCES_SESSION_STORAGE_KEY =
  'research_session_data_sources';

// API defaults for filters (refreshed every 24 hours)
export const RESEARCH_FILTERS_LOCAL_STORAGE_KEY = 'research_filters_v1';

// User's actual filter selections (persists across days)
export const USER_PREFERENCE_FILTERS = 'user_preference_filters_v1';

// Timestamp for 24-hour refresh check (milliseconds since epoch)
export const FILTERS_TIMESTAMP = 'filters_timestamp_v1';

// Gear preferences localStorage key
export const GEAR_PREFERENCES_LOCAL_STORAGE_KEY = 'lexomat-gear-preferences';

// File upload limits — must match backend PDF processing limits in settings.py:
//   MAX_INDIVIDUAL_FILE_SIZE  → backend MAX_INLINE_PDF_SIZE_MB (45MB raw per file)
//   MAX_QUERY_PROCESSING_SIZE → backend MAX_TOTAL_INLINE_REQUEST_SIZE_MB (90MB encoded ÷ 1.33 = ~67MB raw total)
export const MAX_QUERY_PROCESSING_SIZE = 67 * 1024 * 1024; // 67MB raw ≈ 90MB encoded (matches backend MAX_TOTAL_INLINE_REQUEST_SIZE_MB)
export const MAX_INDIVIDUAL_FILE_SIZE = 45 * 1024 * 1024; // 45MB per file (matches backend MAX_INLINE_PDF_SIZE_MB)
export const MAX_FILES_COUNT = 10; // Maximum number of files that can be uploaded

export const SUPABASE_USER_DATA_SOURCE_UPLOAD_BUCKET = 'users';

// =============================================================================
// UNIFIED STORAGE FOLDER STRUCTURE
// =============================================================================
// All user files are stored under a single "Lexomat" parent folder.
// This simplifies quota calculation, cleanup, and GDPR compliance.
//
// Structure:
//   users/{user_id}/Lexomat/
//     ├── uploads/     - User-initiated uploads via Storage page
//     ├── chat/        - Files uploaded during chat sessions
//     ├── research/    - Files uploaded during research sessions
//     └── documents/   - Files uploaded for document analysis

/** Parent folder name for all user files (single source of truth for storage) */
const LEXOMAT_FOLDER_NAME = 'Lexomat';

/** Subfolder names within Lexomat folder */
const LEXOMAT_SUBFOLDERS = {
  /** Direct uploads via Storage page */
  UPLOADS: 'uploads',
  /** Files uploaded during chat sessions (auto-save feature) */
  CHAT: 'chat',
  /** Files uploaded during research sessions */
  RESEARCH: 'research',
  /** Files uploaded for document analysis */
  DOCUMENTS: 'documents',
} as const;

/**
 * Get the full folder path for a specific subfolder type.
 * Returns path like "Lexomat/chat" for use with getOrCreateFolder.
 */
function getLexomatFolderPath(
  subfolder: (typeof LEXOMAT_SUBFOLDERS)[keyof typeof LEXOMAT_SUBFOLDERS]
): string {
  return `${LEXOMAT_FOLDER_NAME}/${subfolder}`;
}

// Legacy folder names (for backwards compatibility during migration)
// TODO: Remove after migration is complete
/** @deprecated Use LEXOMAT_SUBFOLDERS.CHAT instead */
export const CHAT_UPLOADS_FOLDER_NAME = getLexomatFolderPath(
  LEXOMAT_SUBFOLDERS.CHAT
);

/** @deprecated Use LEXOMAT_SUBFOLDERS.DOCUMENTS instead */
export const DOCUMENT_ANALYSIS_FOLDER_NAME = getLexomatFolderPath(
  LEXOMAT_SUBFOLDERS.DOCUMENTS
);

// Video configuration
export const VIDEO_CONFIG = {
  ABOUT_US_VIDEO_URL: 'https://www.youtube.com/embed/IBfinsQ2_M0?vq=hd1080',
};

// =============================================================================
// PROFESSIONAL DIRECTORY (find-a-professional page)
// =============================================================================

// localStorage cache keys + TTLs for directory reference data (changes daily)
export const PROFESSION_TYPES_CACHE_KEY = 'profession_types_cache';
export const PROFESSION_TYPES_CACHE_TTL = 24 * 60 * 60 * 1000; // 1 day
export const REGIONS_CACHE_KEY = 'sk_regions_cache';
export const REGIONS_CACHE_TTL = 24 * 60 * 60 * 1000; // 1 day
// Stage 2b taxonomy tree — replaces the flat spec vocabulary for the directory
// filter. Same TTL: the taxonomy is deploy-gated on the backend so the cache
// window only needs to survive a single deploy.
export const TAXONOMY_CACHE_KEY = 'specialization_taxonomy_cache';
export const TAXONOMY_CACHE_TTL = 24 * 60 * 60 * 1000; // 1 day
// Strict taxonomy-ID gate (Stage-2b): below this result count, show the
// "Iba N výsledkov v tejto kategórii" disclosure instead of silently
// returning a sparse list — the gate never falls back to broader retrieval.
export const LOW_RESULTS_IN_CATEGORY_THRESHOLD = 3;

// Capital-city aliases per Slovak region code — lets a region selection also
// match professionals listed under that region's capital city.
export const REGION_CAPITAL_ALIASES: Record<string, string[]> = {
  'SK-BL': ['Bratislava'],
  'SK-TA': ['Trnava'],
  'SK-NI': ['Nitra'],
  'SK-TC': ['Trenčín'],
  'SK-ZI': ['Žilina'],
  'SK-BC': ['Banská Bystrica'],
  'SK-PV': ['Prešov'],
  'SK-KI': ['Košice'],
};

// Calendly meeting scheduling URL — change here to update everywhere in the app.
export const CALENDLY_URL = 'https://calendly.com/lexomat/30min';

// Map a directory basePath to the next-intl pathname template for detail pages.
export const DETAIL_PATHNAME_MAP: Record<string, '/advokati/[id]'> = {
  '/professional-directory': '/advokati/[id]',
  '/advokati': '/advokati/[id]',
  '/lawyers': '/advokati/[id]',
};

// =============================================================================
// DOCUMENT GENERATION (/generovanie)
// =============================================================================

/** Fixed minimum width (px) for the resizable left sidebar, regardless of window size. */
const DOCGEN_SIDEBAR_WIDTH_MIN = 200;

/** Fraction of the window width used for the sidebar's max/default sizing on desktop. */
const DOCGEN_SIDEBAR_MAX_FRACTION = 0.7;
const DOCGEN_SIDEBAR_DEFAULT_FRACTION = 0.4;

/**
 * Window-relative sidebar width constraints (px): min is a fixed 200px, max is
 * 70% of the window width, default is 40% of the window width. Falls back to
 * a fixed value during SSR/before mount (no `window`), matching the fixed
 * min - callers on the client should recompute after mount and on resize.
 */
export function getDocGenSidebarWidth(): {
  MIN: number;
  MAX: number;
  DEFAULT: number;
} {
  if (typeof window === 'undefined') {
    return {
      MIN: DOCGEN_SIDEBAR_WIDTH_MIN,
      MAX: 900,
      DEFAULT: 400,
    };
  }
  return {
    MIN: DOCGEN_SIDEBAR_WIDTH_MIN,
    MAX: Math.round(window.innerWidth * DOCGEN_SIDEBAR_MAX_FRACTION),
    DEFAULT: Math.round(window.innerWidth * DOCGEN_SIDEBAR_DEFAULT_FRACTION),
  };
}

/** localStorage key for persisting the user's chosen docgen sidebar width. */
export const DOCGEN_SIDEBAR_WIDTH_STORAGE_KEY = 'docgen-sidebar-width';
